import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { setDownloadPresets } from '../../store/reducers/user';
import ConversionDialogShell from './ConversionDialogShell';
import useAssetConversionForm from './useAssetConversionForm';
import { copyStrategy } from './conversionDialogStrategies';

/**
 * Copy-with-conversion dialog — thin wrapper that wires the copy strategy into the
 * shared form hook and shell.
 */
function AssetsCopyDialog(props) {
  const {
    destroy,
    shouldFormatBeDisabled,
    onConfirm,
    onCancel,
    sourceMimeType,
    copyPreviewAssetId,
    duplicateSelectionCount,
    downloadPresets,
    setDownloadPresets: updateDownloadPresets,
  } = props;

  const handleSubmit = useCallback((payload) => {
    if (typeof onConfirm === 'function') onConfirm(payload);
    destroy();
  }, [destroy, onConfirm]);

  const handleClose = useCallback(() => {
    if (typeof onCancel === 'function') onCancel();
    destroy();
  }, [destroy, onCancel]);

  const form = useAssetConversionForm({
    strategy: copyStrategy,
    shouldFormatBeDisabled,
    assets: null,
    selectionCount: duplicateSelectionCount,
    copyPreviewAssetId,
    downloadPresets,
    updateDownloadPresets,
    subscriptionFeatures: {},
    onSubmit: handleSubmit,
    onClose: handleClose,
  });

  return (
    <ConversionDialogShell
      form={form}
      strategy={copyStrategy}
      sourceMimeType={sourceMimeType}
      assets={null}
      copyPreviewAssetId={copyPreviewAssetId}
      selectionCount={duplicateSelectionCount}
      shouldFormatBeDisabled={shouldFormatBeDisabled}
    />
  );
}

AssetsCopyDialog.defaultProps = {
  shouldFormatBeDisabled: false,
  onConfirm: null,
  onCancel: null,
  downloadPresets: [],
  sourceMimeType: undefined,
  copyPreviewAssetId: undefined,
  duplicateSelectionCount: 1,
};

AssetsCopyDialog.propTypes = {
  destroy: PropTypes.func.isRequired,
  shouldFormatBeDisabled: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  downloadPresets: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
  })),
  setDownloadPresets: PropTypes.func.isRequired,
  sourceMimeType: PropTypes.string,
  copyPreviewAssetId: PropTypes.string,
  duplicateSelectionCount: PropTypes.number,
};

export default connect(
  (state) => ({
    downloadPresets: Array.isArray(state.user.presets) ? state.user.presets : [],
  }),
  { setDownloadPresets },
)(AssetsCopyDialog);
